/**
 * Logistics Dashboard (L1)
 * Version: 1.0.0
 * Description:
 *   Real-time overview of current delivery runs, live location of drivers,
 *   and critical rejection alerts with photographic proof.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Chip,
  Avatar,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Badge,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  Timeline as RunIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as RejectionIcon,
  MyLocation as MapIcon,
  Refresh as RefreshIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { logisticsAPI } from '../../api';

// --- Types ---
interface DriverStatus {
  id: string;
  full_name: string;
  vehicle_number: string;
  lat: number;
  lng: number;
  last_ping: string;
  current_run_id?: string;
}

interface RejectionAlert {
  id: string;
  customer_name: string;
  unique_id: string;
  driver_name: string;
  reason: string;
  photo_url?: string;
  timestamp: string;
}

// --- Component ---
export default function LogisticsDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const [drivers, setDrivers] = useState<DriverStatus[]>([]);
  const [rejections, setRejections] = useState<RejectionAlert[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch Live Data
  const fetchLiveData = async () => {
    try {
      setLoading(true);
      const locations = await logisticsAPI.getDashboardLocations();
      setDrivers(locations || []);
      
      // In a real app, this would be WebSocket triggered. 
      // For now we fetch on refresh.
    } catch (error) {
      enqueueSnackbar('Failed to fetch real-time data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 30000); // 30s refresh
    return () => clearInterval(interval);
  }, [enqueueSnackbar]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Live Dispatch Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitoring {drivers.length} active delivery drivers in the field.
          </Typography>
        </Box>
        <IconButton onClick={fetchLiveData} color="primary" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Real-time Rejection Feed (Flashier for attention) */}
      <Alert 
        severity="error" 
        icon={<ErrorIcon />}
        sx={{ mb: 4, borderRadius: 2, display: rejections.length > 0 ? 'flex' : 'none' }}
      >
        <AlertTitle sx={{ fontWeight: 700 }}>Critical Rejections Detected</AlertTitle>
        Latest <strong>{rejections.length}</strong> full rejections require manual intervention.
      </Alert>

      <Grid container spacing={3}>
        {/* Left Column: Summary & Driver Stats */}
        <Grid item xs={12} md={4}>
           <Stack spacing={3}>
              {/* Summary Cards */}
              <Card sx={{ p: 2, borderRadius: 2, backgroundColor: 'primary.main', color: 'white' }}>
                 <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><RunIcon /></Avatar>
                    <Box>
                       <Typography variant="h4" sx={{ fontWeight: 700 }}>{drivers.filter(d => d.current_run_id).length}</Typography>
                       <Typography variant="body2" sx={{ opacity: 0.8 }}>Active Delivery Runs</Typography>
                    </Box>
                 </Stack>
              </Card>

              {/* Driver List */}
              <Card sx={{ borderRadius: 2 }}>
                 <Box p={2} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Active Fleet</Typography>
                    <Badge color="success" variant="dot" invisible={drivers.length === 0} />
                 </Box>
                 <Divider />
                 <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                    {drivers.map((driver) => (
                       <ListItem key={driver.id} divider>
                          <ListItemAvatar>
                             <Avatar sx={{ bgcolor: 'secondary.light' }}><TruckIcon fontSize="small" /></Avatar>
                          </ListItemAvatar>
                          <ListItemText 
                             primary={driver.full_name} 
                             secondary={`${driver.vehicle_number} • Last seen ${new Date(driver.last_ping).toLocaleTimeString()}`}
                             primaryTypographyProps={{ sx: { fontWeight: 600 } }}
                          />
                          <Chip label="Tracking" size="small" variant="outlined" color="primary" icon={<MapIcon />} />
                       </ListItem>
                    ))}
                    {drivers.length === 0 && (
                       <Box sx={{ p: 4, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary">No drivers currently active.</Typography>
                       </Box>
                    )}
                 </List>
              </Card>
           </Stack>
        </Grid>

        {/* Right Column: Live Map Placeholder */}
        <Grid item xs={12} md={8}>
           <Card sx={{ 
              borderRadius: 2, 
              height: 600, 
              display: 'flex', 
              flexDirection: 'column',
              backgroundColor: 'rgba(0,0,0,0.02)',
              position: 'relative',
              overflow: 'hidden'
           }}>
              {/* Map UI Overlay */}
              <Box sx={{ 
                 position: 'absolute', 
                 top: 20, 
                 right: 20, 
                 zIndex: 10,
                 backgroundColor: 'white',
                 p: 1.5,
                 borderRadius: 2,
                 boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}>
                 <Typography variant="caption" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MapIcon fontSize="inherit" color="primary" /> VITE_GOOGLE_MAPS_API_KEY Required
                 </Typography>
              </Box>

              <Box sx={{ 
                 flexGrow: 1, 
                 display: 'flex', 
                 flexDirection: 'column', 
                 alignItems: 'center', 
                 justifyContent: 'center',
                 p: 4,
                 textAlign: 'center'
              }}>
                 <MapIcon sx={{ fontSize: 80, color: 'text.disabled', opacity: 0.2, mb: 2 }} />
                 <Typography variant="h6" color="text.disabled">Live Interactive Map</Typography>
                 <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 400 }}>
                    Google Maps integration is being prepared. Once API credentials are provided, 
                    this view will show real-time driver trails and geofence status.
                 </Typography>
              </Box>
              
              {/* Fake Legend */}
              <Box p={2} sx={{ backgroundColor: 'white', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                 <Stack direction="row" spacing={3}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                       <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main' }} />
                       <Typography variant="caption">On Schedule</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                       <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main' }} />
                       <Typography variant="caption">Full Rejection</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                       <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
                       <Typography variant="caption">Idle {' > '} 15m</Typography>
                    </Stack>
                 </Stack>
              </Box>
           </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
